import { FeatureCollection, Point } from 'geojson'
import { AnyLayer, FillLayer, LineLayer, SymbolLayer } from 'mapbox-gl'

import { City } from '@/model/City'
import { TimeStep } from '@/model/TimeStep'
import { IMapService } from '@/services/map/IMapService'
import { MapBoxGL } from '../MapBoxGL'
import { IController } from './IController'
import { BaseRepresentationController } from './BaseRepresentationController'
import { IControllerParent } from './IControllerParent'
import { Representation } from '@/model/Representation'
import { CumulativeRainfall } from '@/model/CumulativeRainFall'

const DEFAULT_SOURCE = 'cumulativerainfall'

export class CumulativeRainfallController
  extends BaseRepresentationController<Representation<'cumulative rainfall'>>
  implements IController
{
  constructor(
    parent: IControllerParent,
    mapBoxGL: MapBoxGL,
    mapService: IMapService,
    representation: Representation<'cumulative rainfall'>,
  ) {
    super(parent, mapBoxGL, mapService, representation)
    this.cumulativeRainfall = representation.cumulativeRainfall
  }

  private cumulativeRainfall: CumulativeRainfall

  async load(city: City, timeStep?: TimeStep, zoomOnSource = false, scale = 500): Promise<boolean> {
    let result = true

    if (!timeStep) {
      return Promise.resolve(false)
    }

    const requestedTimestamp = timeStep.timestamp.toISO()

    let geoJson = (await this.mapService?.fetchCumulativeRainfall(
      city,
      timeStep,
      this.cumulativeRainfall.cumulativeTimeMinutes,
      this.cumulativeRainfall.cumulAgregationTimeMinutes,
    )) as FeatureCollection
    if (!geoJson) {
      geoJson = this.emptyGeoJson
      result = false
    }

    const currentTimeStep = this.parent.getCurrentTimeStep()
    if (!timeStep.isMax() && currentTimeStep && requestedTimestamp !== currentTimeStep.timestamp.toISO()) {
      return result
    }

    this.mapBoxGL.showOnlyLayers([
      ...this.getLayers().map((layer) => layer.id),
      `${this.mapBoxGL.applicationPrefix}-raingaugestext`,
      `${this.mapBoxGL.applicationPrefix}-selectedraingauges`,
      `${this.mapBoxGL.applicationPrefix}-unselectedraingauges`,
      `${this.mapBoxGL.applicationPrefix}-cityContours*`,
    ])

    this.mapBoxGL.updateSource(this.getSource(), geoJson)
    if (zoomOnSource) {
      this.mapBoxGL.zoom(geoJson)
    }

    return result
  }

  getSources(): string[] {
    return [this.getSource()]
  }

  getSource(): string {
    return `${this.cumulativeRainfall.cumulAgregationTimeMinutes}-${this.cumulativeRainfall.cumulativeTimeMinutes}-${DEFAULT_SOURCE}`
  }

  getLayers(): AnyLayer[] {
    const propertyName = this.representation.dynamicPropertyName
    return [
      {
        id: `${this.mapBoxGL.applicationPrefix}-${this.cumulativeRainfall.cumulativeTimeMinutes}-cumulativerainfall`,
        type: 'fill',
        source: this.getSource(),
        minzoom: 7,
        paint: {
          'fill-opacity': 0.8,
          'fill-color': [
            'case',
            ['<', ['get', propertyName], this.representation.legend.thresholds[0].value],
            'transparent',
            ['interpolate', ['linear'], ['get', propertyName], ...this.unpackThresholds(this.representation.legend)],
          ],
        },
      } as FillLayer,
    ]
  }

  getLayersWithPopup(): AnyLayer[] {
    return [this.layers[0]]
  }

  getCursors(): Map<string, string> {
    return new Map([
      [
        `${this.mapBoxGL.applicationPrefix}-${this.cumulativeRainfall.cumulativeTimeMinutes}-cumulativerainfall`,
        'pointer',
      ],
    ])
  }

  initializeLayers(): void {
    const layers = this.getLayers()
    for (const layer of layers) {
      this.layers.push(layer)
      this.mapBoxGL.addLayer(layer, `${this.mapBoxGL.applicationPrefix}-cityContours-administrativeAreasContours`)
      //as this gets initialized later, we need to render cumulative rainfall layer explicitely behind another layer
    }
  }
}
